import React, { useMemo } from 'react';
import { NavHashLink } from 'react-router-hash-link';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { ReactComponent as HomeIcon } from 'assets/images/svg/home.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/images/svg/chevron-right.svg';
import { ReactComponent as CalendarIcon } from 'assets/images/svg/calendar-icon.svg';
import { ReactComponent as OffersIcon } from 'assets/svg/offers-icon.svg';
import { ReactComponent as GearIcon } from 'assets/images/svg/gear.svg';
import { ReactComponent as GpsIcon } from 'assets/images/svg/gps.svg';
import { ReactComponent as HeartOutlineIcon } from 'assets/images/svg/heart-outline.svg';
import { ReactComponent as NationalMembershipIcon } from 'assets/svg/national-membership.svg';
import { ReactComponent as SpecialsIcon } from 'assets/images/svg/specials-icon.svg';
import { ReactComponent as LocationIcon } from 'assets/images/svg/location.svg';
import { ReactComponent as PaymentsIcon } from 'assets/images/svg/payments.svg';
import { ReactComponent as StoreIcon } from 'assets/images/svg/icons/store-icon.svg';
import { ReactComponent as UserProfile } from 'assets/images/svg/user-profile.svg';

import container from 'containers/HeaderMenuContainer';
import NavItem from 'components/layout/navbar/NavItem';

import {
  ACCOUNT_SETTINGS,
  MY_MEMBERSHIP,
  NETWORK_OFFERS,
  BOOKINGS,
  COMMUNICATION_PREFERENCES,
  EXPLORE_PREFIX,
  FAQS, FAVORITES,
  FREE_MARKETPLACE,
  HOME, MY_PROFILE,
  PAYMENT_METHODS,
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
  VIP_PERKS,
  NETWORK_MEMBERSHIP_SALES,
  CANCELLATION_POLICY,
} from 'utils/routes';
import { features } from 'utils/features';
import { UPCOMING } from 'utils/bookingStatus';
import { CURRENT_LOCATION } from 'utils/constants';
import { M } from 'utils/siteStyle';

const NavItemsMobile = ({
  toggleMenu,
  storeUrl,
  setLocation,
  setLocationFilter,
  setNearMeSearch,
  performSearch,
  location,
  setHotDealsSearch,
}) => {
  const status = useSelector(({ site }) => site?.data);

  const renderNearMeLink = useMemo(() => {
    if (location && location.city && location.state) {
      return (
        <button
          id="navbar-menu-near-me"
          className="flex items-center gap-4 cursor-pointer focus:outline-none"
          onClick={() => {
            toggleMenu();
            setLocationFilter({
              ...CURRENT_LOCATION,
              latitude: location.lat,
              longitude: location.lon,
              hierarchizedUrl: location.hierarchizedUrl,
            });
            setNearMeSearch();
            performSearch();
          }}
        >
          <GpsIcon className="w-4 h-4 fill-current text-grey-3" />
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Near me</div>
        </button>
      );
    }
    return (
      <NavHashLink
        id="navbar-menu-near-me"
        to="/#courses-near-me"
        smooth
      >
        <div className="flex items-center gap-4">
          <div><GpsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Near me</div>
        </div>
      </NavHashLink>
    );
  }, [location, performSearch, setLocationFilter, setNearMeSearch, toggleMenu]);

  const renderDealsLink = useMemo(() => {
    if (location && location.city && location.state) {
      return (
        <button
          id="navbar-menu-deals"
          className="flex items-center gap-4 cursor-pointer focus:outline-none"
          onClick={() => {
            toggleMenu();
            setLocationFilter({
              ...CURRENT_LOCATION,
              type: 'City',
              hierarchizedUrl: location.hierarchizedUrl,
              latitude: location.lat,
              longitude: location.lon,
            });
            setHotDealsSearch();
            performSearch('City');
          }}
        >
          <div><SpecialsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Specials</div>
        </button>
      );
    }
    return (
      <NavHashLink
        id="navbar-menu-deals"
        to="/#courses-near-me"
      >
        <div className="flex items-center gap-4">
          <div><SpecialsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Specials</div>
        </div>
      </NavHashLink>
    );
  }, [location, performSearch, setHotDealsSearch, setLocationFilter, toggleMenu]);

  const mobileNavLinks = {
    main: [
      {
        label: 'Home',
        id: 'home',
        href: HOME,
        Icon: HomeIcon,
        condition: true,
        onClick: () => setLocation({}),
      },
      {
        label: 'Shop',
        id: 'store',
        href: storeUrl,
        Icon: StoreIcon,
        forSeo: true,
        condition: features.storeUrl && storeUrl,
      },
      {
        label: 'Bookings',
        id: 'bookings',
        href: BOOKINGS.replace(':status', UPCOMING),
        Icon: CalendarIcon,
        condition: true,
      },
      {
        label: 'Favorites',
        id: 'favorites',
        href: FAVORITES,
        Icon: HeartOutlineIcon,
        condition: true,
      },
      {
        label: 'Payments',
        id: 'payments-methods',
        href: PAYMENT_METHODS,
        Icon: PaymentsIcon,
        condition: true,
      },
      {
        label: 'My Profile',
        id: 'my-profile',
        href: MY_PROFILE,
        Icon: UserProfile,
        condition: true,
      },
      {
        label: M.BT ? 'Golf Time Club' : 'SG Club',
        id: 'my-membership',
        href: MY_MEMBERSHIP,
        Icon: NationalMembershipIcon,
        condition: features.networkMembership && status?.networkMembershipEnabled,
      },
      {
        label: 'My Offers',
        id: 'my-offers',
        href: NETWORK_OFFERS.replace(':status', 'upcoming'),
        Icon: OffersIcon,
        condition: features.networkMembership && status?.networkMembershipEnabled,
      },
      {
        label: 'Settings',
        id: 'account-settings',
        href: ACCOUNT_SETTINGS,
        Icon: GearIcon,
        condition: true,
      },
    ],
    others: [
      {
        condition: features.nearMeNav,
        isCustomNavMenu: true,
        id: 'near-me',
        customMenu: renderNearMeLink,
      },
      {
        condition: features.topDeals,
        id: 'top-deals',
        isCustomNavMenu: true,
        customMenu: renderDealsLink,
      },
      {
        label: 'Explore',
        id: 'explore',
        href: `${EXPLORE_PREFIX}/united-states`,
        Icon: LocationIcon,
        condition: features.exploreCountriesStates,
      },
      {
        label: 'Gift Cards',
        id: 'gift-cards',
        href: 'https://info.supremegolf.com/supreme-golf-gift-cards/',
        Icon: PaymentsIcon,
        condition: features.giftCardLink,
        forSeo: true,
      },
      {
        label: 'Club',
        id: 'membership',
        href: NETWORK_MEMBERSHIP_SALES,
        Icon: NationalMembershipIcon,
        condition: features.networkMembership && status?.networkMembershipEnabled,
      },
    ],
    footerLinks: [
      {
        label: 'List My Course',
        id: 'list-my-course',
        href: 'https://courses.supremegolf.com/tee-time-distribution/?utm_source=supremegolf.com&utm_medium=referral&utm_campaign=homepage',
        Icon: ChevronRightIcon,
        forSeo: true,
        condition: process.env.REACT_APP_NAME === 'supremeGolf',
      },
      {
        label: 'Supreme Golf Newsletter',
        id: 'supreme-golf-newsletter',
        href: COMMUNICATION_PREFERENCES,
        Icon: ChevronRightIcon,
        condition: process.env.REACT_APP_NAME === 'supremeGolf',
      },
      {
        label: 'List My Course',
        id: 'list-my-course-marketplace',
        href: FREE_MARKETPLACE,
        Icon: ChevronRightIcon,
        condition: features.courseOperators && features.courseOperatorsFreeMarketplace,
      },
      {
        label: 'VIP Perks',
        id: 'vip-perks',
        href: VIP_PERKS,
        Icon: ChevronRightIcon,
        condition: features.courseOperators && features.courseOperatorsVipPerks,
      },
      {
        label: 'Barstool Golftime Newsletter',
        id: 'barstool-golftime-newsletter',
        href: COMMUNICATION_PREFERENCES,
        Icon: ChevronRightIcon,
        condition: process.env.REACT_APP_NAME === 'barstool',
      },
      {
        label: 'Cancellation Options',
        id: 'cancellation-options',
        href: CANCELLATION_POLICY,
        Icon: ChevronRightIcon,
        condition: true,
      },
      {
        label: 'Help Center',
        id: 'help-center',
        href: FAQS,
        Icon: ChevronRightIcon,
        condition: true,
      },
      {
        label: 'Privacy Policy',
        id: 'privacy-policy',
        href: PRIVACY_POLICY,
        Icon: ChevronRightIcon,
        condition: true,
      },
      {
        label: 'Terms of Service',
        id: 'terms-of-services',
        href: TERMS_AND_CONDITIONS,
        Icon: ChevronRightIcon,
        condition: true,
      },
    ],
  };

  const renderNavLinks = (title, links) => (
    <div className={classNames('px-6', {})}>
      <h1 className="pb-10 leading-4 font-primary text-4 -tracking-0.2 text-dark">{title}</h1>
      <ul className="list-none px-4 flex flex-col gap-10">
        {links.map((link) => {
          if (link?.isCustomNavMenu && link?.condition) return <li key={`navbar-menu-${link.id}`}>{link.customMenu}</li>;

          return link?.condition && (
            <li key={link.id} onClick={toggleMenu} aria-hidden>
              <NavItem link={link} />
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderFooterLinks = (
    <div className="bg-white-two px-6 py-10 mt-10">
      <ul className="list-none flex flex-col gap-10">
        {mobileNavLinks.footerLinks.map((link) => link?.condition && (
          <li key={link.id} onClick={toggleMenu} aria-hidden>
            <NavItem link={link} isFooterLink />
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section className="pt-4-5 bg-white">
      {renderNavLinks(process.env.REACT_APP_SHORT_NAME, mobileNavLinks.main)}
      <div className="w-full h-px my-10 bg-pale-grey" />
      {renderNavLinks('Everything Else', mobileNavLinks.others, true)}
      {renderFooterLinks}
    </section>
  );
};

NavItemsMobile.defaultProps = {
  storeUrl: null,
  toggleMenu: null,
  location: null,
  setHotDealsSearch: null,
};

NavItemsMobile.propTypes = {
  toggleMenu: PropTypes.func,
  location: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    hierarchizedUrl: PropTypes.string,
  }),
  performSearch: PropTypes.func.isRequired,
  setLocation: PropTypes.func.isRequired,
  setLocationFilter: PropTypes.func.isRequired,
  setNearMeSearch: PropTypes.func.isRequired,
  storeUrl: PropTypes.string,
  setHotDealsSearch: PropTypes.func,
};

export default container(NavItemsMobile);
